import { useCallback, useRef } from 'react';
import type { MutableRefObject } from 'react';
import type { GridColumn } from './EditableGrid';

/**
 * Opt-in commit auto-save for EditableGrid (`autoSaveCells`): saves a row as
 * soon as a `select` or `date` cell commits a value, instead of waiting for a
 * click outside the row.
 *
 * Per-row debounce timers — each new change on a row resets its timer; the
 * delay also lets the parent's state update (and any mirrored fields, e.g.
 * signoff dates) flush into dataRef first. If a blur/row-switch save beats the
 * timer, the dirty check makes the firing a no-op — no double save.
 */
export function useCommitAutoSave(opts: {
  enabled: boolean;
  columns: GridColumn[];
  dataRef: MutableRefObject<Record<string, unknown>[]>;
  dirtyRowsRef: MutableRefObject<Set<number>>;
  onRowSave: (row: Record<string, unknown>) => void;
  clearRowDirty: (rowIndex: number) => void;
}) {
  const { enabled, columns, dataRef, dirtyRowsRef, onRowSave, clearRowDirty } = opts;
  const timersRef = useRef<Map<number, number>>(new Map());

  const cancel = useCallback((rowIndex: number) => {
    const t = timersRef.current.get(rowIndex);
    if (t !== undefined) {
      window.clearTimeout(t);
      timersRef.current.delete(rowIndex);
    }
  }, []);

  const schedule = useCallback((rowIndex: number, delay: number) => {
    cancel(rowIndex);
    timersRef.current.set(rowIndex, window.setTimeout(() => {
      timersRef.current.delete(rowIndex);
      if (!dirtyRowsRef.current.has(rowIndex)) return;
      const row = dataRef.current[rowIndex];
      if (row) onRowSave(row);
      clearRowDirty(rowIndex);
    }, delay));
  }, [cancel, dataRef, dirtyRowsRef, onRowSave, clearRowDirty]);

  /** Call after every cell change; decides whether/when to auto-save the row. */
  const onCellCommitted = useCallback((rowIndex: number, field: string, value: unknown) => {
    if (!enabled) return;
    const type = columns.find((c) => c.field === field)?.type;
    if (type === 'select') {
      // Dropdown picks are atomic commits — save right away.
      schedule(rowIndex, 250);
    } else if (type === 'date') {
      // Native date inputs fire change per completed segment. A cleared value
      // ('') and any sane-year date are savable; a year like 0002 means the
      // user is typing digit-by-digit — hold the save (and cancel any pending
      // one, which would read the half-typed row) until the value completes.
      // The on-blur save still catches whatever remains held.
      const year = typeof value === 'string' && value ? Number(value.slice(0, 4)) : null;
      if (value === '' || (year !== null && year >= 1000)) schedule(rowIndex, 800);
      else cancel(rowIndex);
    }
  }, [enabled, columns, schedule, cancel]);

  return { onCellCommitted };
}
